<script>
import BaseDropdown from '@/components/_base-dropdown-menu.vue'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import Interaction from '@/models/general/interaction'
import InteractionType from '@/shared/constants/core/InteractionType'
import EmitEvent from '@/models/general/emitEvent'
import CandidateMenuItem from '@/models/candidate/candidateMenuItem.js'
import TextModel from '@/models/general/textModel'
import Spinner from '@/components/general/spinner.vue'
import dateMixin from '@/mixins/dateMixin'

export default {
  components: { BaseDropdown, CandidateImage, Spinner },
  mixins: [dateMixin],
  props: {
    tableItem: {
      type: Object,
      required: true,
    },
    candidateOptions: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    grey600: VuetifyColourClass.grey600,
    blue100: VuetifyColourClass.blue100,
    blue400: VuetifyColourClass.blue400,
    text: VuetifyColourClass.text,
  }),
  computed: {
    isLoading() {
      return !this.candidateOptions
    },
    selectedMenuId() {
      const selectedMenuItem = this.dropdownMenuItems.find(
        ({ candidateId }) => candidateId === this.tableItem.candidateId
      )
      return selectedMenuItem?.id || null
    },
    isUnfilled() {
      const { candidateId, name } = this.tableItem
      return candidateId < 0 || name === 'Unfilled' || !candidateId || !name
    },
    dropdownMenuItems() {
      return [
        new CandidateMenuItem({
          icon: 'mdi-account-circle',
          type: 'action',
          candidateId: null,
          text: this.$t('booking.extend.unfilled'),
          subtitle: this.$t('booking.extend.unfilledSubtitle', [
            this.$t('app.companyNameShort'),
          ]),
          interaction: this.createInteraction(
            null,
            this.$t('booking.extend.unfilled')
          ),
        }),

        ...this.candidateDropdownOptions,
      ]
    },
    candidateDropdownOptions() {
      return this.candidateOptions.map((item) => {
        const unavailable = item.unavailability.some(
          (unavailability) =>
            this.isSame(unavailability.dateLocal, this.tableItem.dateMeta) &&
            unavailability.times.some(
              (block) =>
                this.isBefore(
                  this.tableItem.startTime,
                  block.endTime,
                  this.durationUnits.MINUTE
                ) &&
                this.isBefore(
                  block.startTime,
                  this.tableItem.endTime,
                  this.durationUnits.MINUTE
                )
            )
        )

        const subtitle = unavailable
          ? new TextModel(this.$t('booking.extend.unavailable'), [
              this.text.error,
            ])
          : ''

        return new CandidateMenuItem({
          icon: 'mdi-account-circle',
          type: 'action',
          text: item.name,
          interaction: this.createInteraction(item.id, item.name),
          candidateId: item.id,
          fullName: item.name,
          subtitle,
        })
      })
    },
    disableDropdown() {
      return !!this.tableItem?.removed
    },
  },
  methods: {
    setCandidate(item) {
      this.$emit('setCandidate', item)
      this.$refs.baseDropdown.closeMenu()
    },
    disableCandidateOption(menuItem) {
      return menuItem.subtitle.text === this.$t('booking.extend.unavailable')
    },

    createInteraction(candidateId, name) {
      return new Interaction({
        type: InteractionType.emitEvent,
        value: new EmitEvent({
          event: 'setCandidate',
          value: { candidateId, name },
        }),
      })
    },
  },
}
</script>

<template>
  <BaseDropdown
    ref="baseDropdown"
    :disabled="isLoading || disableDropdown"
    :menu-items="dropdownMenuItems"
    :icon-color="grey600"
    :disable-list-item="disableCandidateOption"
    :selected-id="selectedMenuId"
    width="288"
    border-radius="8px"
    icon-size="35"
    secondary
    candidate-list
    compact-icons
    subtitles
    @setCandidate="setCandidate"
  >
    <template v-slot:activator="{ on, attrs, isActive }">
      <BaseButton
        secondary
        width="288"
        :disabled="disableDropdown"
        class="d-flex align-center mx-1"
        :color="isActive ? blue100 : ''"
        v-bind="{ ...attrs }"
        v-on="on"
      >
        <div class="d-flex justify-space-between align-center w-100">
          <div class="d-flex align-center">
            <v-icon
              v-if="isUnfilled"
              size="35"
              class="mr-4 align-unfilled-icon"
              :color="grey600"
              >mdi-account-circle</v-icon
            >
            <CandidateImage
              v-else
              :id="tableItem.candidateId"
              size="sm"
              :full-name="tableItem.name"
            />
            <p class="mb-0" :class="text.grey500">{{
              isUnfilled ? $t('booking.extend.unfilled') : tableItem.name
            }}</p>
          </div>
          <v-spacer />
          <v-icon
            v-if="!isLoading"
            :color="isActive ? blue400 : grey600"
            class="icon--transition"
            :class="{
              'icon--rotate': isActive,
            }"
            >mdi-menu-down</v-icon
          >
          <div v-else>
            <Spinner size="15" :color="grey600" :width="1" />
          </div>
        </div>
      </BaseButton>
    </template>
  </BaseDropdown>
</template>

<style scoped lang="scss">
.icon--rotate {
  transform: rotate(180deg);
}
.icon--transition {
  transition: transform 0.2s ease-in-out;
}
</style>
